import type { HomeFilterUiState } from "@/utils/homeSearchParams";
import { readHomeSearch } from "@/utils/homeSearchParams";

export type QuoteSelectionMode = "manual" | "auto";

export type RequestQuoteDraftLocation = {
  address: string;
  lat: number;
  lng: number;
};

export type RequestQuoteDraftProvider = {
  userId: string;
  profileId: string;
  name: string;
  avatar?: string;
};

export type RequestQuoteDraftForm = {
  description: string;
  budgetDetails: string;
  urgency: "low" | "medium" | "high";
  selectedCategoryId: string;
  selectedService: string;
  categoryName?: string;
  serviceName?: string;
  startDateISO: string | null;
  location: RequestQuoteDraftLocation | null;
  imagePreviewUrls: string[];
};

export type RequestQuoteDraft = {
  filters: {
    apiPayload: Record<string, unknown>;
    ui: HomeFilterUiState;
  };
  form: RequestQuoteDraftForm;
  mode: QuoteSelectionMode;
  selectedProviders: RequestQuoteDraftProvider[];
  mobileStep: "form" | "providers";
};

const SESSION_KEY = "jandi:requestQuoteDraft";

/** Files cannot live in sessionStorage — kept in memory for the tab session. */
let imageFilesMemory: File[] = [];

export function getRequestQuoteImageFiles(): File[] {
  return imageFilesMemory;
}

export function setRequestQuoteImageFiles(files: File[]) {
  imageFilesMemory = files;
}

export function clearRequestQuoteImageFiles() {
  imageFilesMemory = [];
}

const emptyForm = (): RequestQuoteDraftForm => ({
  description: "",
  budgetDetails: "",
  urgency: "low",
  selectedCategoryId: "",
  selectedService: "",
  categoryName: "",
  serviceName: "",
  startDateISO: null,
  location: null,
  imagePreviewUrls: [],
});

export function createEmptyRequestQuoteDraft(
  filters?: RequestQuoteDraft["filters"]
): RequestQuoteDraft {
  return {
    filters: filters || { apiPayload: {}, ui: {} },
    form: emptyForm(),
    mode: "auto",
    selectedProviders: [],
    mobileStep: "form",
  };
}

export function readRequestQuoteDraft(): RequestQuoteDraft | null {
  try {
    const raw =
      typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem(SESSION_KEY)
        : null;
    if (!raw) return null;
    return JSON.parse(raw) as RequestQuoteDraft;
  } catch {
    return null;
  }
}

export function writeRequestQuoteDraft(draft: RequestQuoteDraft) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(draft));
  } catch (e) {
    console.error("Failed to persist request quote draft", e);
  }
}

export function clearRequestQuoteDraft() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  clearRequestQuoteImageFiles();
}

export function patchRequestQuoteDraft(
  patch: Partial<RequestQuoteDraft>
): RequestQuoteDraft {
  const current = readRequestQuoteDraft() || createEmptyRequestQuoteDraft();
  const next: RequestQuoteDraft = {
    ...current,
    ...patch,
    filters: patch.filters || current.filters,
    form: patch.form ? { ...current.form, ...patch.form } : current.form,
  };
  writeRequestQuoteDraft(next);
  return next;
}

/**
 * Persist home filters (and optional provider) before login so post-login
 * `/request-quote?redirect` lands on compose with the same search context.
 */
export function seedRequestQuoteDraftFromHomeSearch(options?: {
  selectedProvider?: RequestQuoteDraftProvider | null;
}): RequestQuoteDraft {
  const home = readHomeSearch();
  const filters = {
    apiPayload: { ...(home?.apiPayload || {}) },
    ui: { ...(home?.ui || {}) },
  };
  const draft = createEmptyRequestQuoteDraft(filters);
  if (options?.selectedProvider?.userId) {
    draft.mode = "manual";
    draft.selectedProviders = [options.selectedProvider];
  }
  writeRequestQuoteDraft(draft);
  return draft;
}

/** Login URL that returns the client to the request-quote compose flow. */
export function getRequestQuoteLoginUrl(): string {
  return `/ingresar?redirect=${encodeURIComponent("/request-quote")}`;
}
